import React, { useState, useEffect, useRef } from 'react';
import { Loader2, Search, Calendar, Hash, DollarSign, Building, Truck, ScrollText, MessageSquare, FileText, RefreshCw } from 'lucide-react';
import { Autocomplete } from './CreateOrder';
import { searchOrganizations, searchSuppliers } from '../services/api'; // Используем общие функции поиска
import { getProcessingOrders } from '../services/processingApi'; // API для получения заказов на переработку
import { Organization, Supplier, ProcessingOrder } from '../types';

interface ProcessingOrdersProps {
  onOrderSelect?: (order: ProcessingOrder) => void; // Колбэк при клике на строку заказа
}

export const ProcessingOrders = ({ onOrderSelect }: ProcessingOrdersProps) => {
  // Состояния фильтров
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [commentSearch, setCommentSearch] = useState('');
  
  // Состояния списка и загрузки
  const [orders, setOrders] = useState<ProcessingOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false); // Был ли выполнен первый поиск

  // Ref для хранения текущего контроллера отмены запроса (AbortController)
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Функция загрузки заказов
  const fetchOrders = async (orgKey?: string, suppKey?: string, comment?: string) => {
    // 1. Если есть активный запрос, отменяем его
    if (abortControllerRef.current) {
        abortControllerRef.current.abort();
    }
    // 2. Создаем новый контроллер
    const controller = new AbortController();
    abortControllerRef.current = controller;

    console.log("ProcessingOrders: fetchOrders initiated.");
    setIsLoading(true);
    
    try {
      // Выполняем запрос к API, передавая signal для возможности отмены
      const data = await getProcessingOrders(orgKey, suppKey, controller.signal, comment);
      console.log(`ProcessingOrders: Data received. ${data.length} items.`);
      setOrders(data);
      setHasSearched(true); 
    } catch (e: any) {
      // Если ошибка вызвана отменой запроса, игнорируем её
      if (e.name === 'AbortError') {
          console.log("ProcessingOrders: Fetch aborted (likely new search or unmount).");
          return; // Не обновляем стейт
      }
      console.error("ProcessingOrders: Error fetching", e);
      setOrders([]);
      setHasSearched(true);
    } finally {
      // Снимаем флаг загрузки только если это все еще актуальный запрос
      if (abortControllerRef.current === controller) {
         setIsLoading(false);
      }
    }
  };

  // Эффект при монтировании компонента (загружаем список сразу)
  useEffect(() => {
    console.log("ProcessingOrders: Component MOUNTED.");
    fetchOrders();

    // Функция очистки (cleanup) при размонтировании
    return () => {
        console.log("ProcessingOrders: Component UNMOUNTED. Aborting pending requests.");
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
    };
  }, []);

  // Обработчик кнопки поиска
  const handleSearchClick = () => {
    fetchOrders(organization?.Ref_Key, supplier?.Ref_Key, commentSearch.trim());
  };

  // Форматирование даты
  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Расчет суммы (либо из шапки, либо сумма строк)
  const calculateTotalSum = (order: ProcessingOrder) => {
    if (order.СуммаДокумента !== undefined && order.СуммаДокумента !== null) {
      return order.СуммаДокумента;
    }
    return order.Запасы?.reduce((acc, item) => acc + (item.Сумма || 0), 0) || 0;
  };

  // Форматтер валюты (IDR)
  const fmtMoney = new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      
      {/* Шапка с фильтрами */}
      <div className="p-4 border-b border-gray-100 bg-gray-50/50">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          {/* Автокомплит Организации */}
          <Autocomplete<Organization>
            label="Organization"
            placeholder="All Organizations"
            onSearch={searchOrganizations}
            onSelect={setOrganization}
            displayValue={(item) => item.Description}
            initialValue={organization}
            className="w-full"
          />
          {/* Автокомплит Поставщика */}
          <Autocomplete<Supplier>
            label="Supplier"
            placeholder="All Suppliers"
            onSearch={searchSuppliers}
            onSelect={setSupplier}
            displayValue={(item) => item.Description}
            initialValue={supplier}
            className="w-full"
          />

          <div>
            <label htmlFor="processing-order-comment" className="mb-1 block text-sm font-medium text-gray-700">Comment</label>
            <input
              id="processing-order-comment"
              type="text"
              value={commentSearch}
              onChange={(event) => setCommentSearch(event.target.value)}
              placeholder="Search comment"
              className="h-[38px] w-full rounded border border-gray-300 bg-white px-3 text-sm text-gray-900 shadow-sm outline-none focus:border-[#0078d4] focus:ring-1 focus:ring-[#0078d4]"
            />
          </div>
          
          <div className="flex items-center gap-2">
            {/* Кнопка Поиска */}
            <button
              onClick={handleSearchClick}
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-2 rounded bg-[#0078d4] px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-blue-600 disabled:opacity-70 h-[38px]"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Find Orders
            </button>
            
            {/* Кнопка Обновить (Refresh) */}
            <button
              onClick={handleSearchClick}
              disabled={isLoading}
              title="Refresh List"
              className="flex items-center justify-center gap-2 rounded bg-white border border-gray-300 px-3 py-2 text-gray-700 shadow-sm transition hover:bg-gray-50 hover:text-[#0078d4] disabled:opacity-70 h-[38px]"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Таблица результатов */}
      <div className="overflow-x-auto w-full">
        <table className="w-full text-sm text-left min-w-[1000px] table-fixed">
          <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 font-medium w-[5%]"><div className="flex items-center gap-1"><Calendar className="w-3 h-3"/> Date</div></th>
              <th className="px-4 py-3 font-medium w-[5%]"><div className="flex items-center gap-1"><Hash className="w-3 h-3"/> Number</div></th>
              <th className="px-4 py-3 font-medium w-[10%]"><div className="flex items-center gap-1"><Building className="w-3 h-3"/> Organization</div></th>
              <th className="px-4 py-3 font-medium w-[15%]"><div className="flex items-center gap-1"><Truck className="w-3 h-3"/> Supplier</div></th>
              <th className="px-4 py-3 font-medium w-[15%]"><div className="flex items-center gap-1"><ScrollText className="w-3 h-3"/> Contract</div></th>
              <th className="px-4 py-3 font-medium w-[15%]"><div className="flex items-center gap-1"><MessageSquare className="w-3 h-3"/> Comment</div></th>
              <th className="px-4 py-3 font-medium text-right w-[20%]"><div className="flex items-center justify-end gap-1"><DollarSign className="w-3 h-3"/> Total</div></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
               // Состояние загрузки
               <tr>
                 <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                   <div className="flex flex-col items-center justify-center gap-2">
                     <Loader2 className="h-6 w-6 animate-spin text-[#0078d4]" />
                     <span>Loading orders...</span>
                   </div>
                 </td>
               </tr>
            ) : orders.length > 0 ? (
              // Список заказов
              orders.map((order) => {
                return (
                  <tr 
                    key={order.Ref_Key}
                    onClick={() => onOrderSelect && onOrderSelect(order)}
                    className="hover:bg-blue-50/30 transition-colors cursor-pointer"
                    title="Click to view details"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 truncate">
                      {formatDate(order.Date)}
                    </td>
                    <td className="px-4 py-3 font-medium text-[#0078d4] truncate">
                      {order.Number}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 font-medium truncate">
                      {order.Organization_Description ?? order.Организация_Key}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 font-medium truncate">
                      {order.Supplier_Description ?? order.Контрагент_Key}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 truncate">
                      {order.Contract_Number || '-'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 italic truncate">
                      {order.Комментарий || '-'}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-700 whitespace-nowrap truncate">
                      {fmtMoney.format(calculateTotalSum(order))}
                    </td>
                  </tr>
                );
              })
            ) : (
              // Состояние "Нет данных"
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                  {hasSearched ? (
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="h-8 w-8 opacity-20" />
                      No processing orders found.
                    </div>
                  ) : (
                    "Select filters and click Find Orders"
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
